import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Box, ButtonBase, Popover, Stack, Typography } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import { UserInitialsAvatar } from "Components/_Shared/UserInitialsAvatar";
import { StyledValidationText } from "Forms/Components/FormStyles";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatClientName } from "Infrastructure/Utils/ClientUtils";
import { format } from "date-fns";
import { type FC, useState } from "react";
import type { PersonPickerPerson } from "./personPickerPersons";

const POPOVER_MIN_WIDTH = 300;
const POPOVER_MAX_WIDTH = 480;
// MUI's own default keeps the popover 16 px from each viewport edge
const POPOVER_VIEWPORT_GAP = 32;
// matches the "small" UserInitialsAvatar
const EMPTY_AVATAR_SIZE = 32;
const ICON_SIZE = 18;
const CHEVRON_SIZE = 16;
const OPTION_MIN_HEIGHT = 52;

type Props = {
  readonly value: string;
  readonly persons: PersonPickerPerson[];
  readonly onChange: (value: string) => void;
  /** Heading of the popover, e.g. what the person is picked for. */
  readonly caption?: string;
  readonly placeholder?: string;
  readonly required?: boolean;
  readonly error?: string;
  readonly disabled?: boolean;
};

const PersonName: FC<{ readonly person: PersonPickerPerson; readonly fontWeight: number; readonly isTruncated: boolean }> = ({
  person,
  fontWeight,
  isTruncated,
}) => {
  const name = formatClientName(person.firstName, person.surname, null);

  return (
    <Box flex="1 1 auto" minWidth={0} textAlign="left">
      <Typography variant="body2" fontWeight={fontWeight} noWrap={isTruncated} title={isTruncated ? name : undefined}>
        {name}
      </Typography>
      {person.birthDate && (
        <Typography variant="caption" component="p" color="text.secondary" noWrap>
          {format(new Date(person.birthDate), "d. M. yyyy")}
        </Typography>
      )}
    </Box>
  );
};

export const PersonPicker: FC<Props> = ({ value, persons, onChange, caption, placeholder, required = false, error, disabled = false }) => {
  const { t } = useResource();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const selectedPerson = persons.find((person) => person.value === value);
  const isOpen = !!anchorEl;

  const select = (personValue: string) => {
    onChange(personValue);
    setAnchorEl(null);
  };

  return (
    <Box minWidth={0}>
      <ButtonBase
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-invalid={!!error}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={(theme) => ({
          gap: Spacing.M,
          // as wide as its content, so a short name keeps the chevron next to it; at most the whole container,
          // the negative margin only moves the content in line with the container's edge
          maxWidth: `calc(100% + ${theme.spacing(Spacing.M)})`,
          py: Spacing.XS,
          px: Spacing.M,
          ml: -Spacing.M,
          borderRadius: Radius.M,
          "&:hover, &[aria-expanded='true']": { bgcolor: "grey.50" },
          "&.Mui-focusVisible": { outline: `2px solid ${theme.palette.primary.main}` },
        })}
      >
        {selectedPerson ? (
          <>
            <UserInitialsAvatar
              variant="small"
              color={selectedPerson.isMainClient ? "primary" : "secondary"}
              firstName={selectedPerson.firstName}
              surname={selectedPerson.surname}
            />
            <PersonName person={selectedPerson} fontWeight={500} isTruncated />
          </>
        ) : (
          <>
            <Flex
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              width={EMPTY_AVATAR_SIZE}
              height={EMPTY_AVATAR_SIZE}
              borderRadius={Radius.MAX}
              sx={(theme) => ({ bgcolor: theme.colors.textTertiary })}
            >
              <PersonOutlineIcon sx={{ fontSize: ICON_SIZE, color: "text.secondary" }} />
            </Flex>
            <Typography variant="body2" color="text.secondary" noWrap flex="1 1 auto" minWidth={0} textAlign="left">
              {placeholder ?? t(Resources.PersonPicker.Placeholder)}
              {required && (
                <Box component="span" color="error.main">
                  {" *"}
                </Box>
              )}
            </Typography>
          </>
        )}
        <ExpandMoreIcon sx={{ fontSize: CHEVRON_SIZE, color: "text.secondary", flexShrink: 0 }} />
      </ButtonBase>

      {error && <StyledValidationText>{error}</StyledValidationText>}

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              // grows with the longest name; a name longer than the max width wraps instead of being cut
              width: "max-content",
              minWidth: POPOVER_MIN_WIDTH,
              maxWidth: `min(${POPOVER_MAX_WIDTH}px, calc(100% - ${POPOVER_VIEWPORT_GAP}px))`,
              mt: Spacing.S,
              p: Spacing.L,
              border: 1,
              borderColor: "divider",
              borderRadius: Radius.L,
              boxShadow: 2,
            },
          },
        }}
      >
        {caption && (
          <Typography variant="caption" component="p" fontWeight={700} color="text.secondary" textTransform="uppercase" mb={Spacing.M}>
            {caption}
          </Typography>
        )}

        <Stack spacing={Spacing.XS}>
          {persons.map((person) => {
            const isSelected = person.value === value;

            return (
              <ButtonBase
                key={person.value}
                aria-pressed={isSelected}
                onClick={() => select(person.value)}
                sx={(theme) => ({
                  justifyContent: "flex-start",
                  gap: Spacing.M,
                  minHeight: OPTION_MIN_HEIGHT,
                  px: Spacing.M,
                  border: 1,
                  borderColor: isSelected ? theme.colors.brandLightBorder : "transparent",
                  borderRadius: Radius.M,
                  bgcolor: isSelected ? "primary.light" : "transparent",
                  "&:hover": { bgcolor: isSelected ? "primary.light" : "grey.50" },
                  "&.Mui-focusVisible": { outline: `2px solid ${theme.palette.primary.main}` },
                })}
              >
                <UserInitialsAvatar
                  variant="medium"
                  color={person.isMainClient ? "primary" : "secondary"}
                  firstName={person.firstName}
                  surname={person.surname}
                />
                <PersonName person={person} fontWeight={isSelected ? 500 : 400} isTruncated={false} />
                {isSelected && <CheckIcon sx={{ fontSize: ICON_SIZE, color: "primary.main", flexShrink: 0 }} />}
              </ButtonBase>
            );
          })}
        </Stack>
      </Popover>
    </Box>
  );
};
